<template>
    <Transition name="slide-down">
        <div v-if="showWarning" class="multi-tab-warning">
            <div class="multi-tab-warning__content">
                <div class="multi-tab-warning__icon">
                    <TheIcon name="warning" />
                </div>
                <div class="multi-tab-warning__message">
                    <strong>{{ $t('components.multi-tab-warning.title') }}</strong>
                    <p>{{ $t('components.multi-tab-warning.message') }}</p>
                    <p class="multi-tab-warning__details">
                        {{ $t('components.multi-tab-warning.active-tabs') }}: {{ tabCount }}
                    </p>
                </div>
                <button 
                    class="multi-tab-warning__close" 
                    @click="dismissWarning"
                    :aria-label="$t('components.multi-tab-warning.close')">
                    <TheIcon name="close" />
                </button>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTabSync } from '@/composables/useTabSync'
import TheIcon from '@/components/atoms/TheIcon/TheIcon.vue'

const route = useRoute()
const tabSync = useTabSync()
const { activeTabs } = storeToRefs(tabSync)

const dismissed = ref(false)

// Routes where tab warning should be active
// Only during the 3-step event creation flow
const SENSITIVE_ROUTES = [
    'createEventStep1',             // Step 1 of event creation
    'createEventStep2',             // Step 2 of event creation
    'createEventStep3',             // Step 3 of event creation (confirmation)
    'create-event'                  // Parent route for event creation
]

// Check if current route is sensitive
const isOnSensitiveRoute = computed(() => {
    return SENSITIVE_ROUTES.includes(route.name as string)
})

// Count how many tabs are on create-event routes
const tabsOnCreateEventRoutes = computed(() => {
    return Array.from(activeTabs.value.values()).filter(tabInfo => 
        tabInfo.routeName && SENSITIVE_ROUTES.includes(tabInfo.routeName)
    )
})

// Only show warning when on create-event route AND there are multiple tabs on create-event routes
const showWarning = computed(() => {
    return isOnSensitiveRoute.value && 
           !dismissed.value && 
           tabsOnCreateEventRoutes.value.length > 1
})

const tabCount = computed(() => tabsOnCreateEventRoutes.value.length)

// Reset dismissal when tab count changes or when navigating away from sensitive route
watch([tabsOnCreateEventRoutes, isOnSensitiveRoute], ([newTabsOnRoute, newIsOnSensitive]) => {
    if (newTabsOnRoute.length <= 1) {
        // Reset dismissal when we're back to single tab on create-event routes
        dismissed.value = false
    }
    if (!newIsOnSensitive) {
        // Reset dismissal when navigating away from sensitive routes
        dismissed.value = false
    }
})

function dismissWarning() {
    dismissed.value = true
}
</script>

<style scoped lang="scss">
.multi-tab-warning {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    
    &__content {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1rem 2rem;
        max-width: 1400px;
        margin: 0 auto;
    }
    
    &__icon {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        
        :deep(svg) {
            width: 20px;
            height: 20px;
            fill: white;
        }
    }
    
    &__message {
        flex: 1;
        
        strong {
            display: block;
            font-size: 1.1rem;
            margin-bottom: 0.25rem;
        }
        
        p {
            margin: 0;
            font-size: 0.95rem;
            opacity: 0.95;
        }
    }
    
    &__details {
        font-size: 0.85rem !important;
        margin-top: 0.25rem !important;
        opacity: 0.8 !important;
    }
    
    &__close {
        flex-shrink: 0;
        background: rgba(255, 255, 255, 0.2);
        border: none;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: background 0.2s ease;
        
        &:hover {
            background: rgba(255, 255, 255, 0.3);
        }
        
        :deep(svg) {
            width: 16px;
            height: 16px;
            fill: white;
        }
    }
}

// Transition animations
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-enter-from {
    transform: translateY(-100%);
    opacity: 0;
}

.slide-down-leave-to {
    transform: translateY(-100%);
    opacity: 0;
}

@media (max-width: 768px) {
    .multi-tab-warning {
        &__content {
            padding: 0.75rem 1rem;
            gap: 0.75rem;
        }
        
        &__icon {
            width: 28px;
            height: 28px;
            
            :deep(svg) {
                width: 16px;
                height: 16px;
            }
        }
        
        &__message {
            strong {
                font-size: 1rem;
            }
            
            p {
                font-size: 0.85rem;
            }
        }
        
        &__details {
            display: none; // Hide tab count on mobile
        }
        
        &__close {
            width: 28px;
            height: 28px;
        }
    }
}
</style>
